import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { Observable, from, of } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { OrdersApiService } from 'src/app/builder-services/api/orders.api.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { ShippingApiService } from 'src/app/builder-services/api/shipping.api.service'
import { IntegrationsService } from 'src/app/integrations/integrations.service'
import { environment } from 'src/environments/environment'
import * as XLSX from 'xlsx'
export interface OrdersTableColumns {
  [key: string]: any // This allows for dynamic fields
}
@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  lastIndex: number = 0
  orderStatuses: any[] = []
  selectedSort: string = 'id_desc';
  ordersDataSource = new MatTableDataSource<OrdersTableColumns>([]);
  allOrders: OrdersTableColumns[] = []
  selectedType: string = 'all'
  orderData: any[] = []
  pageIndex = 0
  pageSize = 5
  totalCount = 0
  paginationCount = 0
  /** Every order placed by one customer. */
  getUserOrders(userId: string): Promise<any[]> {
    return this.ordersApi.getUserOrders(String(this.builderService.selectedProject), userId)
  }

  /** Removes a single order-metadata row by key. */
  deleteOrderMetadata(orderId: string, key: string): Promise<void> {
    return this.ordersApi.deleteOrderMetadata(String(this.builderService.selectedProject), orderId, key)
  }

  constructor(
    private builderService: BuilderService,
    private ordersApi: OrdersApiService,
    private settingsApi: SettingsApiService,
    private shippingApi: ShippingApiService,
    private http: HttpClient,
    private integrationsService: IntegrationsService
  ) {}

  getOrder(orderId: string): Observable<any> {
    const projectId = this.builderService.selectedProject
    return from(this.ordersApi.getOrder(projectId, orderId))
  }

  addLastIndex(_lastIndex: number) { /* no-op — hum_id is derived from Supabase MAX(hum_id)+1 */ }

  getCurrentTime() {
    let currentTime = new Date().toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
    return currentTime
  }


  setFincartOrder(fincartOrderId: any, fincartOrderCode: any, trackURL: any, order_id: any) {
    this.ordersApi.upsertOrderFincartData(String(this.builderService.selectedProject), order_id, {
      fincartId: fincartOrderId,
      fincartCode: fincartOrderCode,
      trackingUrl: trackURL,
    }).catch((err: any) => console.warn('[OrdersService] setFincartOrder:', err))
  }

  /**
   * Creates an Aymakan shipment for an order through stacks-agents. The Aymakan
   * API key is decrypted server-side, so the browser never handles it.
   * @param payload - Project, order, declared value and recipient details.
   * @returns Observable emitting the created shipment with tracking number and label URL.
   */
  createAymakanShipment(payload: {
    projectId: string
    orderId: string
    declaredValue: number
    pieces?: number
    recipient: {
      name: string
      phone: string
      city: string
      address: string
      countryCode: string
      email?: string
      postalCode?: string
    }
  }): Observable<{ shipmentId: string; trackingNumber?: string; labelUrl?: string }> {
    return this.http.post<{ shipmentId: string; trackingNumber?: string; labelUrl?: string }>(
      `${environment.agentsUrl}shipping/aymakan/shipment`,
      payload
    )
  }

  /**
   * Creates a shipment for an order through the active shipping provider.
   * The provider is detected server-side; API credentials are never sent to the browser.
   * @param payload - Project, order, declared value and recipient details.
   * @returns Observable emitting the created shipment with tracking number and label URL.
   */
  createShipment(payload: {
    projectId: string
    orderId: string
    declaredValue: number
    pieces?: number
    isCod?: boolean
    codAmount?: number
    recipient: {
      name: string
      phone: string
      city: string
      address: string
      countryCode: string
      email?: string
      postalCode?: string
    }
  }): Observable<{ shipmentId: string; trackingNumber?: string; labelUrl?: string }> {
    return this.http.post<{ shipmentId: string; trackingNumber?: string; labelUrl?: string }>(
      `${environment.agentsUrl}shipping/shipment`,
      payload
    )
  }

  /**
   * Persists the Aymakan tracking number and label URL onto the order metadata.
   * @param orderId - The Supabase order ID.
   * @param data - The tracking number and label URL returned by Aymakan.
   */
  setAymakanShipment(orderId: any, data: { trackingNumber?: string; labelUrl?: string }) {
    this.ordersApi.upsertOrderAymakanData(String(this.builderService.selectedProject), orderId, data)
      .catch((err: any) => console.warn('[OrdersService] setAymakanShipment:', err))
  }

  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${this.builderService.getToken()}` })
  }

  /**
   * Manually attributes an order to a Meta Ads campaign/adset — for chat-closed
   * sales (e.g. the Instagram Messenger agent) that Meta's Pixel/Insights API
   * never sees, so the meta-ads campaign analysis can count them.
   *
   * Routed through the stacks-agents backend (not a direct Supabase write, unlike
   * setFincartOrder/setAymakanShipment) because the backend's handler also fires
   * the Meta Conversions API push so Ads Manager's own reporting picks up the
   * conversion too — that only happens on this endpoint, not on a raw DB write.
   */
  setMetaAdsAttribution(orderId: string, campaignId: string, adSetId: string): Observable<{ ok: boolean }> {
    return this.http.patch<{ ok: boolean }>(
      `${environment.agentsUrl}v1/social-inbox/orders/${orderId}/campaign-attribution`,
      { campaign_id: campaignId, ad_set_id: adSetId },
      { headers: this.agentsAuthHeaders() }
    )
  }

  /**
   * Lists Meta Ads campaigns for the current project, for the order attribution
   * picker. Campaign names are not unique — a project can have several
   * active/paused/deleted campaigns sharing the same name — so `status` is
   * returned alongside `name` and must be shown in the picker to tell them apart.
   */
  listMetaAdsCampaigns(): Observable<{ id: string; name: string; status: string }[]> {
    const projectId = this.builderService.selectedProject
    return this.http.get<{ id: string; name: string; status: string }[]>(
      `${environment.agentsUrl}v1/meta-ads/campaigns?projectId=${projectId}`,
      { headers: this.agentsAuthHeaders() }
    )
  }

  /** Lists ad sets belonging to a Meta Ads campaign, for the order attribution picker. */
  listMetaAdsAdSets(campaignId: string): Observable<{ id: string; name: string; status: string }[]> {
    const projectId = this.builderService.selectedProject
    return this.http.get<{ id: string; name: string; status: string }[]>(
      `${environment.agentsUrl}v1/meta-ads/campaigns/${campaignId}/ad-sets?projectId=${projectId}`,
      { headers: this.agentsAuthHeaders() }
    )
  }

  updateOrderStatus = (order_id: string, status: string, _user_id: string, _type?: string) => {
    const currentTime = this.getCurrentTime()
    this.orderStatuses.push({ orderStatus: status, stateChangeTime: currentTime })

    return from(this.ordersApi.updateOrderStatus(String(this.builderService.selectedProject), order_id, status))
  }
  public updateAddress(addressId: any, user_id: any, updatesAddress: any) {
    let project_id = this.builderService.selectedProject
    return new Observable<any>((observer) => {
      this.settingsApi.getSettingsByType(String(project_id), 'user_addresses_' + user_id).then((existing: any) => {
        const addresses = existing || {}
        addresses[addressId] = updatesAddress
        return this.settingsApi.upsertSettingsByType(String(project_id), 'user_addresses_' + user_id, addresses)
      }).then((data: any) => {
        observer.next(data)
        observer.complete()
      }).catch((err: any) => console.warn('[OrdersService] updateAddress:', err))
    })
  }
  public addAddress(user_id: string, address: any) {
    let project_id = this.builderService.selectedProject
    const addressId = String(Date.now())
    return new Observable<any>((observer) => {
      this.settingsApi.upsertSettingsByType(String(project_id), 'user_addresses_' + user_id, { [addressId]: JSON.stringify(address) })
        .then(() => {
          observer.next(address)
          observer.complete()
        })
        .catch((err: any) => console.warn('[OrdersService] addAddress:', err))
    })
  }

  public getShipping = () => {
    let project_id = this.builderService.selectedProject
    return new Observable<any>((observer) => {
      this.shippingApi.getShippingCountries(String(project_id)).then((shipping: any) => {
        observer.next(shipping)
        observer.complete()
      }).catch(() => {
        observer.next([])
        observer.complete()
      })
    })
  }

  exportOrdersToExcel = (orders: any[]) => {
    // if there is no orders
    if (!orders || orders.length === 0) {
      console.warn('No orders to export')
      return
    }

    // Function to convert any value to a string representation
    const formatValue = (value: any): string => {
      if (value === null || value === undefined) {
        return ''
      }

      // Special handling for products array
      if (Array.isArray(value) && value.some((item) => typeof item === 'object' && ('productName' in item || 'quantity' in item || 'regularPrice' in item))) {
        // This is likely a products array
        return value
          .map((product) => {
            const productName = product.productName || product.name || ''
            const regularPrice = product.regularPrice
              ? typeof product.regularPrice === 'object'
                ? Object.entries(product.regularPrice)
                    .map(([key, val]) => `${key}: ${val}`)
                    .join(', ')
                : product.regularPrice
              : ''
            const quantity = product.quantity || ''

            const selectedVariation = product.selectedVariation ? Object.entries(product.selectedVariation) : []
            if (selectedVariation.length > 0) {
              return `productName: ${productName} | regularPrice: ${regularPrice} | Qty: ${quantity} | Variation: ${selectedVariation.map(([key, val]) => `${key}: ${val}`).join(', ')}`
            }
            return `productName: ${productName} | regularPrice: ${regularPrice} | Qty: ${quantity}`
          })
          .join(' || ')
      }
      if (Array.isArray(value)) {
        return value
          .map((item) => {
            if (typeof item === 'object') {
              return Object.entries(item)
                .map(([key, val]) => `${key}: ${val}`)
                .join(', ')
            }
            return String(item)
          })
          .join(' | ')
      }
      if (typeof value === 'object') {
        return Object.entries(value)
          .map(([key, val]) => `${key}: ${formatValue(val)}`)
          .join(', ')
      }
      return String(value)
    }

    // Get all unique keys from all orders
    const allKeys = new Set<string>()
    orders.forEach((order) => {
      Object.keys(order).forEach((key) => allKeys.add(key))
    })

    // Transform orders data dynamically
    const exportData = orders.map((order) => {
      const formattedOrder: { [key: string]: string } = {}
      allKeys.forEach((key) => {
        formattedOrder[key] = formatValue(order[key])
      })
      return formattedOrder
    })

    // Create worksheet
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData)

    // Auto-size columns based on content
    const maxLength = 50 // Maximum column width
    const columnWidths = Array.from(allKeys).map((key) => ({
      wch: Math.min(Math.max(key.length, ...exportData.map((row) => String(row[key]).length)), maxLength)
    }))
    worksheet['!cols'] = columnWidths

    // Create workbook
    const workbook: XLSX.WorkBook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders')

    // Generating filename with current date
    const date = new Date()
    const filename = `orders_export_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`

    // Generating and downloading Excel file (blob approach works reliably in Angular/webpack 5)
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  getStacksOrders(): Observable<any[]> {
    return from(this.ordersApi.getOrders(this.builderService.selectedProject))
  }

  getCompletedOrdersSummary(fromDate?: string, toDate?: string): Observable<{ count: number; revenue: number; orders: any[] }> {
    return from(this.ordersApi.getCompletedOrdersSummary(this.builderService.selectedProject, fromDate, toDate))
  }

  getOrders(): Observable<any[]> {
    return from(this.ordersApi.getOrders(this.builderService.selectedProject))
  }

  fetchCount(type?: string): Observable<number> {
    const t = type && type !== 'all' ? type : undefined
    return from(this.ordersApi.getOrdersCount(this.builderService.selectedProject, t))
  }

  fetchPage(page: number, pageSize: number, type?: string, sort?: string): Observable<any[]> {
    const t = type && type !== 'all' ? type : undefined
    return from(this.ordersApi.getOrdersPage(this.builderService.selectedProject, page, pageSize, t, sort || this.selectedSort))
  }

  /**
   * Sync all WooCommerce orders from WordPress to the Stacks backend.
   *
   * This method sends a GET request to the custom WordPress REST API endpoint `/wp-json/v4/syncAllOrders`.
   * The backend handles formatting and pushing all orders to Stacks automatically.
   */
  syncAllOrdersWP = () => {
    return this.integrationsService.getWordpressSiteUrl().pipe(
      switchMap((url: string) => {
        if (!url) return of(null)
        const wordPressURL = environment.proxyUrl + url + '/wp-json/v4/syncAllOrders'
        return this.http.get(wordPressURL)
      })
    )
  }
  deleteOrder(orderId: string, _userId?: string, _type?: string): Observable<void> {
    return from(this.ordersApi.deleteOrder(String(this.builderService.selectedProject), orderId))
  }

  restoreStock(orderData: any): Observable<any> {
    const projectId = this.builderService.selectedProject
    return from(
      this.ordersApi.restoreOrderStock(projectId, orderData.products || [])
    )
  }
}



